import { motion } from "framer-motion";
import { MapPin, FileText, Landmark, Clock, ExternalLink, Phone, Mail } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useSettings } from "../../hooks/useSettings";

export const CompanyLocation = () => {
    const { t, lang, fs } = useLang();
    const s = useSettings();
    const isAr = lang === "ar";

    const mapsLink = s.maps_link || "https://maps.app.goo.gl/F59X4zQMRKzXPnbV6";
    const mapsEmbed =
        "https://www.google.com/maps?q=" +
        encodeURIComponent(s.maps_query || "AL THURAYA TIRES AUTO SERVICES CO. (MERO), Al Malaz, Riyadh 12644") +
        "&output=embed";
    const landline = s.contact_landline || "0112204999";
    const email = s.contact_email || "";
    const address = isAr
        ? s.address_ar || `${t("loc_addr_1")} — ${t("loc_addr_2")}`
        : s.address_en || `${t("loc_addr_1")} — ${t("loc_addr_2")}`;
    const hours = (isAr ? s.hours_ar : s.hours_en) || "";

    const CREDS = [
        { icon: Landmark, label: t("loc_cr"), value: "7053792292", testid: "company-cr-number" },
        { icon: FileText, label: t("loc_vat"), value: "314664394700003", testid: "company-vat-number" },
        { icon: MapPin, label: t("loc_postal"), value: "12644", testid: "company-postal-code" },
    ];

    return (
        <section id="location" className="relative py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-12"
                >
                    <p className="font-mono-spec text-xs tracking-[0.3em] text-amber-400 mb-3" dir="ltr">
                        {t("loc_eyebrow")}
                    </p>
                    <h2 className={`font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white ${fs("loc_title")}`}>
                        {t("loc_title")}
                    </h2>
                </motion.div>

                <div className="grid lg:grid-cols-5 gap-6">
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-2 space-y-4"
                    >
                        <div className="grid grid-cols-1 gap-4">
                            {CREDS.map((c) => (
                                <div
                                    key={c.label}
                                    className="spotlight-card border border-white/10 rounded-2xl p-5 flex items-center gap-4 hover:border-amber-400/40 transition-colors duration-300"
                                >
                                    <c.icon className="w-6 h-6 text-amber-400 shrink-0" />
                                    <div>
                                        <p className="text-xs text-slate-500">{c.label}</p>
                                        <p
                                            data-testid={c.testid}
                                            className="font-mono-spec font-bold text-white text-lg"
                                            dir="ltr"
                                        >
                                            {c.value}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="spotlight-card border border-white/10 rounded-2xl p-5">
                            <div className="flex items-start gap-4">
                                <MapPin className="w-6 h-6 text-red-500 shrink-0 mt-1" />
                                <div>
                                    <p className="text-xs text-slate-500 mb-1">{t("loc_addr_label")}</p>
                                    <p className="text-sm text-slate-200 leading-relaxed">{address}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4 mt-4 pt-4 border-t border-white/5">
                                <Clock className="w-5 h-5 text-amber-400 shrink-0 mt-1" />
                                <div className="text-sm text-slate-300 space-y-1">
                                    {(hours ? hours.split("|") : [t("loc_hours_1"), t("loc_hours_2")]).map((h) => (
                                        <p key={h}>{h.trim()}</p>
                                    ))}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-3 mt-5">
                                <a
                                    href={mapsLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="open-google-maps-button"
                                    className="flex items-center gap-2 bg-amber-400 text-[#0A1228] text-xs font-extrabold rounded-full px-5 py-2.5 hover:bg-amber-300 transition-colors duration-300"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    {t("loc_open_maps")}
                                </a>
                                <a
                                    href={`tel:${landline}`}
                                    data-testid="landline-call-button"
                                    className="flex items-center gap-2 border border-white/15 text-slate-200 text-xs font-bold rounded-full px-5 py-2.5 hover:border-amber-400/50 transition-colors duration-300"
                                    dir="ltr"
                                >
                                    <Phone className="w-3.5 h-3.5" />
                                    {landline}
                                </a>
                                <a
                                    href={`mailto:${email}`}
                                    data-testid="email-link"
                                    className="flex items-center gap-2 border border-white/15 text-slate-200 text-xs font-bold rounded-full px-5 py-2.5 hover:border-amber-400/50 transition-colors duration-300"
                                >
                                    <Mail className="w-3.5 h-3.5" />
                                    {email}
                                </a>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-3 rounded-3xl overflow-hidden border border-white/10 min-h-[380px] relative"
                        data-testid="shop-map-container"
                    >
                        <iframe
                            title="موقع شركة إطار الثريا لخدمات السيارات - الرياض الملز"
                            src={mapsEmbed}
                            className="absolute inset-0 w-full h-full grayscale-[35%] contrast-[1.05]"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
